import { useState, useEffect, useCallback } from 'react'
import { api } from '../services/api'

interface UseApiListOptions {
  readonly path: string
  readonly limit?: number
  readonly filters?: Record<string, string | undefined>
}

interface UseApiListResult<T> {
  readonly data: readonly T[]
  readonly total: number
  readonly page: number
  readonly loading: boolean
  readonly error: string | null
  readonly setPage: (page: number) => void
  readonly refresh: () => void
}

export function useApiList<T>({ path, limit = 25, filters = {} }: UseApiListOptions): UseApiListResult<T> {
  const [data, setData] = useState<readonly T[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const filterKey = JSON.stringify(filters)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      const parsed = JSON.parse(filterKey) as Record<string, string | undefined>
      for (const [key, value] of Object.entries(parsed)) {
        if (value) params.set(key, value)
      }
      const result = await api.get<readonly T[]>(`${path}?${params}`)
      setData(result.data ?? [])
      setTotal(result.meta?.total ?? 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [path, page, limit, filterKey])

  useEffect(() => {
    fetch()
  }, [fetch])

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [filterKey])

  return { data, total, page, loading, error, setPage, refresh: fetch }
}
